import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface SelectedPhoto {
  id?: number;
  file?: File;
  url: string;
}

interface PhotoReorderProps {
  selectedFiles: SelectedPhoto[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<SelectedPhoto[]>>;
  onOrderChange?: (order: number[]) => void;
}

export function PhotoReorder({
  selectedFiles,
  setSelectedFiles,
  onOrderChange,
}: PhotoReorderProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null) return;

    const newFiles = [...selectedFiles];
    const [draggedItem] = newFiles.splice(draggedIndex, 1);
    newFiles.splice(targetIndex, 0, draggedItem);

    setSelectedFiles(newFiles);
    setDraggedIndex(null);

    // Gerar array de índices ordenados baseado na ordem atual
    const photoOrder = newFiles.map((_, index) => index);
    if (onOrderChange) {
      onOrderChange(photoOrder);
    }
  };

  const handleRemove = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);

    const photoOrder = newFiles.map((_, i) => i);
    if (onOrderChange) {
      onOrderChange(photoOrder);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newFiles = [...selectedFiles];
    [newFiles[index - 1], newFiles[index]] = [
      newFiles[index],
      newFiles[index - 1],
    ];
    setSelectedFiles(newFiles);

    const photoOrder = newFiles.map((_, i) => i);
    if (onOrderChange) {
      onOrderChange(photoOrder);
    }
  };

  const moveDown = (index: number) => {
    if (index === selectedFiles.length - 1) return;
    const newFiles = [...selectedFiles];
    [newFiles[index], newFiles[index + 1]] = [
      newFiles[index + 1],
      newFiles[index],
    ];
    setSelectedFiles(newFiles);

    const photoOrder = newFiles.map((_, i) => i);
    if (onOrderChange) {
      onOrderChange(photoOrder);
    }
  };

  if (selectedFiles.length === 0) {
    return null;
  }

  return (
    <div className="mt-6">
      <Label className="mb-3 block font-semibold text-gray-700">
        Reordenar Fotos (Arraste para reordenar)
      </Label>
      <p className="mb-3 text-sm text-gray-500">
        A primeira foto será exibida como principal no card do imóvel
      </p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {selectedFiles.map((photo, index) => (
          <div
            key={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(index)}
            className={`relative cursor-move rounded-lg border-2 ${
              draggedIndex === index
                ? 'border-blue-500 opacity-50'
                : 'border-gray-300'
            } overflow-hidden bg-white shadow-md transition-all hover:shadow-lg`}
          >
            <div className="aspect-square w-full">
              <img
                src={photo.url}
                alt={`Foto ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-1 text-xs font-bold text-white">
              {index + 1}
            </div>
            <div className="absolute right-2 top-2 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className="rounded-full bg-white/90 p-1 text-gray-700 shadow-md hover:bg-white disabled:opacity-50"
                title="Mover para cima"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => moveDown(index)}
                disabled={index === selectedFiles.length - 1}
                className="rounded-full bg-white/90 p-1 text-gray-700 shadow-md hover:bg-white disabled:opacity-50"
                title="Mover para baixo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
            <Button
              type="button"
              onClick={() => handleRemove(index)}
              variant="destructive"
              size="sm"
              className="absolute bottom-2 right-2 h-6 px-2 text-xs"
            >
              Remover
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
